import axios, { CancelToken } from 'axios';
import { ServiceResponse } from '../../OptimusRoutes/interfaces';
import { DownloadMutimediaDto, MultimediaDto } from './multimedia.dto';

type Service = {
	getEvents: (
		id: number,
		startDate: Date | string,
		endDate: Date | string,
		source?: CancelToken
	) => Promise<ServiceResponse<MultimediaDto[]>>;
	download: (args: DownloadMutimediaDto) => Promise<void>;
	getStreamFileUrl: (id: number) => Promise<ServiceResponse<string>>;
	getLiveStreamUrl: (cameraId: number, channelKey: number) => Promise<ServiceResponse<string>>;
	stopLiveSteam: (cameraId: number, channelKey: number) => Promise<ServiceResponse>;
	getLiveStreamHealth: (cameraId: number, channelKey: number, cancelToken?: CancelToken) => Promise<boolean>;
};

const CHANNEL_HEALTHY_STATUS = 'HEALTHY';

const getEvents = async (
	id: number,
	startDate: Date | string,
	endDate: Date | string,
	source?: CancelToken
): Promise<ServiceResponse<MultimediaDto[]>> => {
	let options = {
		params: {
			'start-date': startDate,
			'end-date': endDate,
		},
		source,
	};

	const { data } = await axios.get<ServiceResponse<MultimediaDto[]>>(`/v2/multimedia/${id}/events`, options);
	return data;
};

const getStreamFileUrl = async (id: number): Promise<ServiceResponse<string>> => {
	const { data } = await axios.get<ServiceResponse<string>>(`/v2/multimedia/${id}/stream`);
	return data;
};

const getLiveStreamUrl = async (cameraId: number, channelKey: number): Promise<ServiceResponse<string>> => {
	const { data } = await axios.get<ServiceResponse<string>>(`/v2/camera/${cameraId}/channel/${channelKey}/live`);
	return data;
};

const stopLiveSteam = async (cameraId: number, channelKey: number): Promise<ServiceResponse> => {
	const { data } = await axios.post<ServiceResponse>(`/v2/camera/${cameraId}/channel/${channelKey}/live`);
	return data;
};

const getLiveStreamHealth = async (
	cameraId: number,
	channelKey: number,
	cancelToken?: CancelToken
): Promise<boolean> => {
	try {
		const {
			data: { success, error },
		} = await axios.get<ServiceResponse<{ health: string }>>(
			`/v2/camera/${cameraId}/channel/${channelKey}/health`,
			{
				cancelToken,
			}
		);
		if (error) {
			return false;
		}
		return success?.health === CHANNEL_HEALTHY_STATUS;
	} catch (error) {
		return false;
	}
};

const download = async ({ id, token, name }: DownloadMutimediaDto): Promise<void> => {
	const { data } = await axios.post<BlobPart>(
		`/v2/multimedia/${id}/download?token=${token}`,
		{},
		{ responseType: 'blob' }
	);
	const url = window.URL.createObjectURL(new Blob([data]));
	const link = document.createElement('a');
	link.href = url;
	link.setAttribute('download', name);
	document.body.appendChild(link);
	link.click();
};

const MultimediaService: Service = {
	getEvents,
	download,
	getStreamFileUrl,
	getLiveStreamUrl,
	stopLiveSteam,
	getLiveStreamHealth,
};

export default MultimediaService;
